import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import ApiService from '../services/ApiService';
import { requiresCheckIn } from '../utils/permissions';
import { broadcastAttendance, onAttendanceUpdate } from '../utils/attendanceBus';
import { formatDuration, formatDurationShort } from '../utils/helper';
import './CheckInOut.css';

/**
 * Header widget for the signed-in admin's work session.
 *
 * Three states, driven entirely by the API's status payload:
 *   1. Check In button        — no session today yet
 *   2. Live HH:MM:SS + Check Out — session open
 *   3. "Done for today" pill  — today's session is checked out
 *
 * One check-in per day is the rule, so state 3 lasts until tomorrow. Both
 * actions confirm first — a mis-click on either one writes a row that only an
 * Attendance Logs admin can delete.
 *
 * The elapsed time is derived from the stored `check_in_at` plus the
 * server/browser clock offset returned by the API, so a skewed or sleeping
 * browser clock cannot inflate or freeze the timer — every tick recomputes from
 * the timestamps rather than incrementing a counter.
 */
const CheckInOut = () => {
    const [state, setState] = useState(null);       // latest status payload
    const [elapsed, setElapsed] = useState(0);      // seconds, recomputed each tick
    const [loading, setLoading] = useState(false);  // check-in/out request in flight
    const [ready, setReady] = useState(false);      // initial status fetch done
    // serverTime - clientTime at the moment of the last API response.
    const clockOffset = useRef(0);

    // Check-in/out is mandatory for Staff (see CheckInGate) but not for the
    // owner account or the built-in "Admin" role, so this widget follows the
    // same rule rather than a togglable permission.
    const canUse = requiresCheckIn();
    const session = state?.checked_in ? state.session : null;

    const applyState = useCallback((data) => {
        if (!data) return;
        if (data.server_time) {
            clockOffset.current = new Date(data.server_time).getTime() - Date.now();
        }
        setState(data);
        setElapsed(data.checked_in ? data.elapsed_seconds || 0 : 0);
    }, []);

    const fetchStatus = useCallback(async () => {
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/attendance/status',
            });
            if (response.data.status) applyState(response.data.data);
        } catch (error) {
            // A failed status check just leaves the widget in its last known
            // state; ApiService already surfaced the error.
        } finally {
            setReady(true);
        }
    }, [applyState]);

    useEffect(() => {
        if (!canUse) return;
        fetchStatus();
    }, [canUse, fetchStatus]);

    // Picks up a check-in/out made elsewhere (the CheckInGate modal, most
    // often) without waiting for the next full page load.
    useEffect(() => {
        if (!canUse) return;
        return onAttendanceUpdate(applyState);
    }, [canUse, applyState]);

    // Tick only while a session is open.
    useEffect(() => {
        if (!session?.check_in_at) return;
        const checkInMs = new Date(session.check_in_at).getTime();
        const tick = () => {
            setElapsed(Math.max(0, Math.floor((Date.now() + clockOffset.current - checkInMs) / 1000)));
        };
        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [session]);

    const handleCheckIn = async () => {
        const result = await Swal.fire({
            title: 'Check in?',
            text: 'This starts your work session for today. You can only check in once per day.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#0f9d58',
            confirmButtonText: 'Yes, check in',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
        });

        if (!result.isConfirmed) return;

        try {
            setLoading(true);
            const response = await ApiService.request({
                method: 'POST',
                url: 'admin/attendance/check-in',
            });
            const data = response.data;
            // A rejected check-in still carries the live state (e.g. another tab
            // already opened a session), so sync from it either way.
            applyState(data.data);
            broadcastAttendance(data.data);
            data.status ? toast.success(data.message) : toast.error(data.message);
        } catch (error) {
            // Handled by ApiService.
        } finally {
            setLoading(false);
        }
    };

    const handleCheckOut = async () => {
        const result = await Swal.fire({
            title: 'Check out?',
            text: `Your session of ${formatDuration(elapsed)} will be closed. You cannot check in again until tomorrow.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#0f9d58',
            confirmButtonText: 'Yes, check out',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
        });

        if (!result.isConfirmed) return;

        try {
            setLoading(true);
            const response = await ApiService.request({
                method: 'POST',
                url: 'admin/attendance/check-out',
            });
            const data = response.data;
            applyState(data.data);
            broadcastAttendance(data.data);
            data.status ? toast.success(data.message) : toast.error(data.message);
        } catch (error) {
            // Handled by ApiService.
        } finally {
            setLoading(false);
        }
    };

    // Roles without the permission never see the control, and nothing renders
    // before the first status call so the button cannot flash the wrong state.
    if (!canUse || !ready || !state) return null;

    // Session open — live timer plus the way to close it.
    if (session) {
        return (
            <div className="app-navbar-item ms-1 ms-lg-3 checkin-widget">
                <div className="checkin-session" title={`Checked in at ${new Date(session.check_in_at).toLocaleTimeString()}`}>
                    <span className="checkin-timer">
                        <span className="checkin-dot"></span>
                        {formatDuration(elapsed)}
                    </span>
                    <button
                        type="button"
                        className="checkout-btn"
                        onClick={handleCheckOut}
                        disabled={loading}
                    >
                        <i className="bi bi-box-arrow-right"></i>
                        <span className="checkin-label">Check Out</span>
                    </button>
                </div>
            </div>
        );
    }

    // Today's session is already closed — show what was logged rather than a
    // dead button, so the rule reads as "done", not "broken".
    if (!state.can_check_in) {
        const today = state.today_session;
        return (
            <div className="app-navbar-item ms-1 ms-lg-3 checkin-widget">
                <div
                    className="checkin-done"
                    title={`Checked out at ${today?.check_out_at ? new Date(today.check_out_at).toLocaleTimeString() : '—'}. Next check-in is available tomorrow.`}
                >
                    <i className="bi bi-check-circle-fill"></i>
                    <span className="checkin-done-time">{formatDurationShort(today?.duration_seconds)}</span>
                    <span className="checkin-label">logged today</span>
                </div>
            </div>
        );
    }

    return (
        <div className="app-navbar-item ms-1 ms-lg-3 checkin-widget">
            <button
                type="button"
                className="checkin-btn"
                onClick={handleCheckIn}
                disabled={loading}
                title="Start your work session"
            >
                <i className="bi bi-box-arrow-in-right"></i>
                <span className="checkin-label">Check In</span>
            </button>
        </div>
    );
};

export default CheckInOut;
